import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import { Layout } from "./components/Layout";
import { Login } from "./pages/Login";
import { Dashboard } from "./pages/Dashboard";
import { Profile } from "./pages/Profile";
import { Projects } from "./pages/Projects";
import { Kanban } from "./pages/Kanban";
import { UserStories } from "./pages/UserStories";
import { UserStoryDetail } from "./pages/UserStoryDetail";
import { Releases } from "./pages/Releases";
import { ReleaseDetail } from "./pages/ReleaseDetail";
import { Ideas } from "./pages/Ideas";
import { Tickets } from "./pages/Tickets";
import { AdminUsers } from "./pages/admin/AdminUsers";
import { AdminProjects } from "./pages/admin/AdminProjects";
import { AdminApiTokens } from "./pages/admin/AdminApiTokens";
import { AdminStatuses } from "./pages/admin/AdminStatuses";
import { AdminActions } from "./pages/admin/AdminActions";
import { AdminAuditLogs } from "./pages/admin/AdminAuditLogs";
import { Documents } from "./pages/Documents";
import config from "./config";

// Set document title from runtime config
const siteTitle = config.SITE_TITLE;

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner h-8 w-8" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (adminOnly && user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }
  
  return <Layout>{children}</Layout>;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner h-8 w-8" />
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function AppRoutes() {
  // Set document title on mount
  useEffect(() => {
    document.title = siteTitle;
  }, []);

  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/projects"
        element={
          <ProtectedRoute>
            <Projects />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/projects/:projectId/kanban"
        element={
          <ProtectedRoute>
            <Kanban />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/user-stories"
        element={
          <ProtectedRoute>
            <UserStories />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/user-stories/:id"
        element={
          <ProtectedRoute>
            <UserStoryDetail />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/ideas"
        element={
          <ProtectedRoute>
            <Ideas />
          </ProtectedRoute>
        }
      />

      <Route
        path="/tickets"
        element={
          <ProtectedRoute>
            <Tickets />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/releases"
        element={
          <ProtectedRoute>
            <Releases />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/releases/:id"
        element={
          <ProtectedRoute>
            <ReleaseDetail />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/documents"
        element={
          <ProtectedRoute>
            <Documents />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/documents/callback"
        element={
          <ProtectedRoute>
            <Documents />
          </ProtectedRoute>
        }
      />
      
      {/* Admin Routes */}
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute adminOnly>
            <AdminUsers />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/admin/projects"
        element={
          <ProtectedRoute adminOnly>
            <AdminProjects />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/admin/api-tokens"
        element={
          <ProtectedRoute adminOnly>
            <AdminApiTokens />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/admin/statuses"
        element={
          <ProtectedRoute adminOnly>
            <AdminStatuses />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/admin/actions"
        element={
          <ProtectedRoute adminOnly>
            <AdminActions />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/admin/audit-logs"
        element={
          <ProtectedRoute adminOnly>
            <AdminAuditLogs />
          </ProtectedRoute>
        }
      />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <NotificationProvider>
          <AppRoutes />
          <Toaster position="bottom-right" />
        </NotificationProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
